/**
 * A class to manage the Power Table UI, including modal creation, table updates, and CSV download functionality.
 * @category Components
 * @subcategory PowerTable
 */
export class PowerTableUI {
  /**
   * Creates an instance of PowerTableUI.
   * @param {DownloadCsv} csvDownloader - An instance of the DownloadCsv class to handle CSV downloads.
   */
  constructor(csvDownloader) {
    this.eventHub = experience.eventHub;
    this.csvDownloader = csvDownloader;
    
  }

  cleanup() {
    console.log("PowerTableUI cleanup started...");
  
    // Remove event listeners
    const downloadButton = document.getElementById("powerTable-download-button");
    if (downloadButton) {
      downloadButton.removeEventListener("click", this.csvDownloader.downloadCSV);
    }
  
    // Remove modal from DOM if exists
    const powerTableModal = document.getElementById("powerTable-modal");
    if (powerTableModal) {
      powerTableModal.remove();
    }
  
    // Clear references
    this.csvDownloader = null;
  
    console.log("PowerTableUI cleanup completed.");
  }
  /**
   * Creates the Power Table modal container with draggable and resizable functionality.
   * @returns {HTMLDivElement} The modal container element.
   */
  createModal() {
    const modalContainer = document.createElement("div");
    modalContainer.className = "pwrTable-container"; // Reuse BOM table styles
    modalContainer.id = "powerTable-modal";

    const draggableContainer = document.createElement("div");
    draggableContainer.className = "powerTable-draggable-container";
    // Title block centered
    const modalTitleBlock = this.createModalTitleBlock();
    draggableContainer.appendChild(modalTitleBlock);

    const table = this.createTable();
    draggableContainer.appendChild(table);

    const actionContainer = this.createActionContainer();
    draggableContainer.appendChild(actionContainer);

    const resizeContainer = this.createResizeContainer();
    modalContainer.appendChild(resizeContainer);

    modalContainer.appendChild(draggableContainer);

    return modalContainer;
  }

  /**
   * Creates the modal title block with a label and close button.
   * @returns {HTMLDivElement} The title block element.
   */
  createModalTitleBlock() {
    const titleBlock = document.createElement("div");
    titleBlock.className = "bomTable-title-block"; // Reuse BOM table styles
    const modalLabel = document.createElement("h2");
    modalLabel.className = "bomTable-modal-label"; // Reuse BOM table styles
    modalLabel.textContent = "Power Table";

    const closeModalBtn = document.createElement("button");
    closeModalBtn.className = "bomTable-close-modal"; // Reuse BOM table styles
    closeModalBtn.innerHTML = "&times;";
    closeModalBtn.onclick = () => {
      const powerModal = document.getElementById("powerTable-modal");
      powerModal.style.display = "none";

      const pwrSwitch = document.getElementById("pwr-switch");
      pwrSwitch.checked = false;
    };

    titleBlock.appendChild(modalLabel);
    titleBlock.appendChild(closeModalBtn);
    return titleBlock;
  }

  /**
   * Creates the table for displaying power data.
   * @returns {HTMLTableElement} The table element.
   */
  createTable() {
    const table = document.createElement("table");
    table.className = "bomTable-part-number-table"; // Reuse BOM table styles
    table.id = "powerTable";

    const header = this.createTableHeader([
      "Part Number",
      "QTY",
      "Volts (V)",
      "Current (mA)",
      "Pwr (mW)",
      "Total Power (mW)",
    ]);
    table.appendChild(header);

    const tbody = document.createElement("tbody");
    tbody.id = "powerTableBody";
    table.appendChild(tbody);

    const tfoot = this.createFooter();
    table.appendChild(tfoot);

    return table;
  }

  /**
   * Creates the table header with specified column names.
   * @param {string[]} headers - The list of column names.
   * @returns {HTMLTableSectionElement} The table header element.
   */
  createTableHeader(headers) {
    const thead = document.createElement("thead");
    const tr = document.createElement("tr");
    headers.forEach((header) => {
      const th = document.createElement("th");
      th.textContent = header;
      tr.appendChild(th);
    });
    thead.appendChild(tr);
    return thead;
  }

  /**
   * Creates the table footer element.
   * @returns {HTMLTableSectionElement} The table footer element.
   */
  createFooter() {
    const tfoot = document.createElement("tfoot");
    tfoot.id = "powerTableFooter";
    return tfoot;
  }

  /**
   * Creates the action container with a download button and an alert div.
   * @returns {HTMLDivElement} The action container element.
   */
  createActionContainer() {
    const actionContainer = document.createElement("div");
    actionContainer.className = "bomTable-action-container"; // Reuse BOM table styles
    const downloadButton = this.createDownloadButton();
    actionContainer.appendChild(downloadButton);

    const alertDiv = this.createAlertDiv();
    actionContainer.appendChild(alertDiv);

    return actionContainer;
  }

  /**
   * Creates the CSV download button.
   * @returns {HTMLButtonElement} The download button element.
   */
  createDownloadButton() {
    const button = document.createElement("button");
    button.classList.add("pwrTableDownloadCsv");
    button.id = "powerTable-download-button";
    button.textContent = "Download CSV";
    button.onclick = () => this.csvDownloader.downloadCSV();
    return button;
  }

  /**
   * Creates the alert div for displaying "No components added" message.
   * @returns {HTMLDivElement} The alert div element.
   */
  createAlertDiv() {
    const alertDiv = document.createElement("div");
    alertDiv.id = "powerTable-alert";
    alertDiv.textContent = "No components added";
    alertDiv.style.display = "none"; // Make sure it's initially hidden
    return alertDiv;
  }

  /**
   * Updates the table with the provided power data.
   * @param {Object} powerData - The power data to display in the table.
   */
  updateTable(powerData) {
    const tbody = document.getElementById("powerTableBody");
    tbody.innerHTML = "";
    powerData.data.forEach((item) => {
      const tr = document.createElement("tr");
      ["partNumber", "qty", "volts", "current", "power", "totalPower"].forEach(
        (key) => {
          const td = document.createElement("td");
          td.textContent = item[key];
          tr.appendChild(td);
        }
      );
      tbody.appendChild(tr);
    });
  }

  /**
   * Updates the footer with the total power value.
   * @param {number} total - The total power value to display.
   */
  updateFooter(total) {
    const footer = document.getElementById("powerTableFooter");
    footer.innerHTML = ""; // Clear existing footer content

    if (total) {
      const tr = document.createElement("tr");
      tr.style.textAlign = "right";

      // Create the first cell that spans the first 4 columns
      const tdSummary = document.createElement("td");
      tdSummary.colSpan = 5;
      tdSummary.textContent = "Total Power (mW)";

      // Second cell for the total power value
      const tdValue = document.createElement("td");
      tdValue.textContent = total;

      // Append cells to the row
      tr.appendChild(tdSummary);
      tr.appendChild(tdValue);

      // Append the row to the footer
      footer.appendChild(tr);
    }
  }

  /**
   * Creates the resize container for resizing the modal.
   * @returns {HTMLDivElement} The resize container element.
   */
  createResizeContainer() {
    // Add the resize container
    const resizeContainer = document.createElement("div");
    resizeContainer.className = "bomTable-resize-footer";
    resizeContainer.style.cssText = `
    position: absolute;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 10px;
    cursor: ns-resize;
  `;
    return resizeContainer;
  }

  /**
   * Clears the table contents.
   */
  clearTable() {
    const tbody = document.getElementById("powerTableBody");
    tbody.innerHTML = "";
    const footer = document.getElementById("powerTableFooter");
    footer.innerHTML = "";
  }
}
